import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Play, Pause, SkipForward, Clock, User, Share2, ArrowLeft } from 'lucide-react';
import { comboApi, getAudioFileUrl } from '@/lib/api';
import { AudioVisualBadge } from '@/components/audio/AudioVisuals';
import { AUDIO_COLORS } from '@/lib/constants';
import { getComboColorHex } from '@/lib/utils';

const ComboPublicPage = () => {
  const { comboId } = useParams();
  const [searchParams] = useSearchParams();
  const [combo, setCombo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    loadCombo();
  }, [comboId]);

  // Autoplay al llegar desde una notificación push (?autoplay=1)
  useEffect(() => {
    if (combo && searchParams.get('autoplay') === '1' && combo.audios?.length > 0) {
      setCurrentIndex(0);
      setIsPlaying(true);
      setProgress(0);
    }
  }, [combo]);

  const loadCombo = async () => {
    try {
      setLoading(true);
      const data = await comboApi.getCombo(comboId);
      // Combos con reglas no tienen audios fijos: generar la selección actual
      if (!data.audios || data.audios.length === 0) {
        try {
          const autoAudios = await comboApi.getAutoAudios(data.combo_id || comboId, 10);
          if (autoAudios && autoAudios.length > 0) {
            data.audios = autoAudios;
          }
        } catch (e) {
          console.error('Error loading auto audios:', e);
        }
      }
      setCombo(data);
    } catch (err) {
      console.error('Error loading combo:', err);
      setError('No se encontró el combo');
    } finally {
      setLoading(false);
    }
  };

  const currentAudio = combo?.audios?.[currentIndex];

  const playAudio = (index) => {
    // Si es el mismo audio que está sonando, toggle play/pause
    if (index === currentIndex && isPlaying) {
      setIsPlaying(false);
      return;
    }
    
    setCurrentIndex(index);
    setIsPlaying(true);
    setProgress(0);
  };

  const playNext = () => {
    if (combo?.audios && currentIndex < combo.audios.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setIsPlaying(true);
      setProgress(0);
    } else {
      setIsPlaying(false);
    }
  };

  useEffect(() => {
    if (audioRef.current && currentAudio) {
      audioRef.current.src = getAudioFileUrl(currentAudio.audio_url);
      if (isPlaying) {
        audioRef.current.play().catch(console.error);
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying, currentAudio]);

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const current = audioRef.current.currentTime;
      const total = audioRef.current.duration || 1;
      setProgress((current / total) * 100);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const totalDuration = combo?.audios?.reduce((sum, a) => sum + (a.duration_seconds || 0), 0) || 0;
  const comboHex = getComboColorHex(combo);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="animate-spin w-10 h-10 border-4 border-purple-400 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error || !combo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex flex-col items-center justify-center p-4">
        <p className="text-white/60 text-lg mb-4">{error || 'Combo no encontrado'}</p>
        <Link to="/" className="bg-purple-500 text-white px-6 py-2 rounded-xl">
          Ir al inicio
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 pb-32">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/5 border-b border-white/10">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/combos" className="text-white/70 hover:text-white">
            <ArrowLeft size={24} />
          </Link>
          <div className="flex-1">
            <h1 className="text-white font-bold">{combo.name || 'Combo'}</h1>
            <p className="text-white/50 text-xs">{combo.audios?.length || 0} audios</p>
          </div>
          <button
            onClick={() => {
              const shareText = `Me he acordado de ti con esto 🤗😅 ${window.location.href}`;
              
              if (navigator.share) {
                navigator.share({
                  text: shareText
                });
              } else {
                navigator.clipboard.writeText(shareText);
                alert('Link copiado!');
              }
            }}
            className="bg-cyan-500 text-white px-3 py-1.5 rounded-full text-sm flex items-center gap-1"
          >
            <Share2 size={14} />
            Compartir
          </button>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6">
        {/* Combo Info Card */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-4 mb-6 border border-white/20">
          <div className="flex items-center gap-3 mb-3">
            <div
              className={`w-14 h-14 rounded-xl flex items-center justify-center ${comboHex ? '' : 'bg-gradient-to-br from-purple-500 to-pink-500'}`}
              style={comboHex ? { backgroundColor: comboHex } : undefined}
            >
              <span className="text-2xl">{combo.emoji || '🎵'}</span>
            </div>
            <div className="flex-1">
              <h2 className="text-white font-bold text-lg">{combo.name}</h2>
              {combo.description && (
                <p className="text-white/60 text-sm">{combo.description}</p>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-4 text-white/50 text-sm">
            <div className="flex items-center gap-1">
              <Clock size={14} />
              <span>{formatTime(totalDuration)}</span>
            </div>
            <div className="flex items-center gap-1">
              <User size={14} />
              <span>@{combo.username || 'AudioToo'}</span>
            </div>
          </div>
        </div>

        {/* Audio List */}
        <div className="space-y-2">
          <h3 className="text-white/70 text-sm font-medium mb-3">Audios del combo:</h3>
          
          {(!combo.audios || combo.audios.length === 0) && (
            <p className="text-white/50 text-sm bg-white/5 rounded-xl p-4 border border-white/10">
              Este combo se genera cada día con los audios más recientes de sus temáticas. Aún no hay contenido suficiente.
            </p>
          )}
          
          {combo.audios?.map((audio, idx) => {
            const colorData = AUDIO_COLORS[audio.color] || AUDIO_COLORS.blue;
            const isCurrentPlaying = idx === currentIndex && isPlaying;
            const isCurrent = idx === currentIndex;
            
            return (
              <div
                key={audio.audio_id}
                onClick={() => playAudio(idx)}
                className={`rounded-xl overflow-hidden cursor-pointer transition-all ${
                  isCurrent ? 'ring-2 ring-purple-400' : 'hover:bg-white/10'
                }`}
              >
                <div className={`flex items-center gap-3 p-3 ${isCurrent ? 'bg-white/15' : 'bg-white/5'}`}>
                  {/* Number */}
                  <span className="text-white/40 text-sm w-5">{idx + 1}</span>
                  
                  {/* Badge: forma del tipo de audio + color de temática */}
                  <AudioVisualBadge
                    shape={audio.shape}
                    color={audio.color}
                    emoji={audio.emoji || colorData.emoji}
                    language={audio.language}
                    size="xs"
                  />
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm truncate">
                      {audio.title || colorData.name}
                    </p>
                    <p className="text-white/50 text-xs">
                      @{audio.username?.split(' ')[0]} • {audio.duration_seconds}s
                    </p>
                  </div>
                  
                  {/* Play button */}
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isCurrentPlaying ? 'bg-purple-500' : 'bg-white/20'
                  }`}>
                    {isCurrentPlaying ? (
                      <Pause size={16} className="text-white" />
                    ) : (
                      <Play size={16} className="text-white ml-0.5" />
                    )}
                  </div>
                </div>

                {/* Progress bar for current */}
                {isCurrent && (
                  <div className="h-1 bg-white/10">
                    <div 
                      className="h-full bg-gradient-to-r from-purple-400 to-pink-500 transition-all duration-200"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </main>

      {/* Mini Player */}
      {currentAudio && (
        <div className="fixed bottom-0 left-0 right-0 bg-black/95 backdrop-blur-xl border-t border-white/10 z-40">
          {/* Progress bar */}
          <div className="h-1 bg-white/10">
            <div 
              className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-200"
              style={{ width: `${progress}%` }}
            />
          </div>

          <div className="max-w-lg mx-auto p-3 flex items-center gap-3">
            <AudioVisualBadge
              shape={currentAudio.shape}
              color={currentAudio.color}
              emoji={currentAudio.emoji || AUDIO_COLORS[currentAudio.color]?.emoji}
              language={currentAudio.language}
              size="sm"
            />
            
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">
                {currentAudio.title || AUDIO_COLORS[currentAudio.color]?.name}
              </p>
              <p className="text-white/50 text-xs">
                {currentIndex + 1} de {combo.audios?.length}
              </p>
            </div>
            
            <button
              onClick={() => setIsPlaying(!isPlaying)}
              className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 ${
                isPlaying ? 'bg-purple-500' : 'bg-white/20'
              }`}
            >
              {isPlaying ? <Pause size={20} className="text-white" /> : <Play size={20} className="text-white ml-0.5" />}
            </button>
            
            <button
              onClick={playNext}
              disabled={currentIndex >= (combo.audios?.length || 0) - 1}
              className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 disabled:opacity-30"
            >
              <SkipForward size={16} className="text-white" />
            </button>
          </div>
          
          <audio
            ref={audioRef}
            onTimeUpdate={handleTimeUpdate}
            onEnded={playNext}
          />
        </div>
      )}
    </div>
  );
};

export default ComboPublicPage;
